import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Users,
  Utensils,
  Accessibility,
  TrendingUp,
  Calendar,
  Clock,
  BarChart3,
  PieChart,
  Heart,
  Sparkles,
  ArrowUpRight,
  ArrowDownRight,
  CheckCircle2,
  XCircle,
  Info,
  Eye,
  Share,
  Download,
  UserPlus,
  Mail,
  MousePointerClick,
  Globe,
  MapPin
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simple donut chart component
const DonutChart = ({ data, size = 120, strokeWidth = 16 }) => {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  if (total === 0) return null;
  
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;
  
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        {data.map((item, idx) => {
          const percentage = item.value / total;
          const strokeDasharray = `${percentage * circumference} ${circumference}`;
          const strokeDashoffset = -offset * circumference;
          offset += percentage;
          
          return (
            <circle
              key={idx}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={item.color}
              strokeWidth={strokeWidth}
              strokeDasharray={strokeDasharray}
              strokeDashoffset={strokeDashoffset}
              className="transition-all duration-500"
            />
          );
        })}
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center">
          <p className="text-2xl font-bold">{total}</p>
          <p className="text-xs text-gray-500">Total</p>
        </div>
      </div>
    </div>
  );
};

// Stat card with trend indicator
const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = "bg-gray-100" }) => (
  <Card>
    <CardContent className="pt-4 pb-3">
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center text-xs ${trend > 0 ? 'text-green-600' : trend < 0 ? 'text-red-600' : 'text-gray-400'}`}>
            {trend > 0 ? <ArrowUpRight className="w-3 h-3" /> : trend < 0 ? <ArrowDownRight className="w-3 h-3" /> : null}
            {trend !== 0 && `${Math.abs(trend)}%`}
          </div>
        )}
      </div>
      <p className="text-2xl font-bold mt-2">{value}</p>
      <p className="text-xs text-gray-500">{title}</p>
      {subtitle && <p className="text-[10px] text-gray-400 mt-0.5">{subtitle}</p>}
    </CardContent>
  </Card>
);

// Interest/preference breakdown bar
const BreakdownBar = ({ items, label }) => {
  const total = items.reduce((sum, i) => sum + i.count, 0);
  if (total === 0) return null;
  
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-gray-700">{label}</p>
      <div className="h-3 rounded-full bg-gray-100 overflow-hidden flex">
        {items.map((item, idx) => (
          <div
            key={idx}
            className="h-full transition-all duration-500"
            style={{ 
              width: `${(item.count / total) * 100}%`,
              backgroundColor: item.color 
            }}
            title={`${item.label}: ${item.count}`}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        {items.filter(i => i.count > 0).map((item, idx) => (
          <div key={idx} className="flex items-center gap-1 text-xs text-gray-600">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} />
            <span>{item.label}</span>
            <span className="text-gray-400">({item.count})</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Host Insights Dashboard
 * Shows pre-RSVP and post-RSVP analytics for event hosts
 */
export default function HostInsightsDashboard({ 
  event, 
  rsvps = [], 
  innerCircleMembers = [],
  waitlistMembers = [],
  hideDietary = false // New prop to hide dietary-related stats for instructor pilots
}) {
  const [activeView, setActiveView] = useState("overview");
  const [prefInsights, setPrefInsights] = useState(null);
  const [prefLoading, setPrefLoading] = useState(false);

  // Fetch the admin-sync'd preference-focused insights from the
  // backend aggregator whenever the event/rsvps count changes.
  useEffect(() => {
    if (!event?.event_id) return;
    let cancelled = false;
    setPrefLoading(true);
    const API_BASE = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("session_token");
    fetch(`${API_BASE}/api/host/insights/preferences/${event.event_id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled) setPrefInsights(data);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setPrefLoading(false); });
    return () => { cancelled = true; };
  }, [event?.event_id, rsvps.length]);
  
  // Calculate pre-RSVP metrics (inner circle / pre-invite signups)
  const preRsvpStats = {
    total: innerCircleMembers.length,
    converted: innerCircleMembers.filter(m => m.converted_to_rsvp).length,
    pending: innerCircleMembers.filter(m => !m.converted_to_rsvp && m.status !== 'rejected').length,
    bySource: innerCircleMembers.reduce((acc, m) => {
      const source = m.source || 'direct';
      acc[source] = (acc[source] || 0) + 1;
      return acc;
    }, {}),
    interests: innerCircleMembers.reduce((acc, m) => {
      (m.interests || []).forEach(interest => {
        acc[interest] = (acc[interest] || 0) + 1;
      });
      return acc;
    }, {})
  };
  
  // Calculate RSVP metrics
  const rsvpStats = {
    total: rsvps.length,
    confirmed: rsvps.filter(r => r.status === 'approved' || r.status === 'confirmed').length,
    pending: rsvps.filter(r => r.status === 'pending').length,
    declined: rsvps.filter(r => r.status === 'cant_make_it' || r.status === 'declined').length,
    checkedIn: rsvps.filter(r => r.status === 'checked_in').length,
    withDietary: rsvps.filter(r => r.dietary_restrictions?.length > 0 || r.dietary_notes).length,
    withAccessibility: rsvps.filter(r => r.accessibility_needs?.length > 0 || r.invisible_disabilities?.length > 0).length,
    totalAttending: rsvps.reduce((sum, r) => {
      if (r.status === 'cant_make_it') return sum;
      return sum + 1 + (r.plus_one_count || r.guests_count || 0);
    }, 0)
  };
  
  // Calculate dietary breakdown
  const dietaryBreakdown = rsvps.reduce((acc, r) => {
    (r.dietary_restrictions || []).forEach(d => {
      acc[d] = (acc[d] || 0) + 1;
    });
    return acc;
  }, {});
  
  // Calculate interests breakdown
  const interestsBreakdown = rsvps.reduce((acc, r) => {
    (r.preferences?.interests || r.interests || []).forEach(i => {
      acc[i] = (acc[i] || 0) + 1;
    });
    return acc;
  }, {});
  
  // Waitlist metrics
  const waitlistStats = {
    total: waitlistMembers.length,
    converted: waitlistMembers.filter(w => w.converted_to_rsvp).length
  };
  
  // RSVP status chart data
  const rsvpStatusData = [
    { label: "Confirmed", value: rsvpStats.confirmed, color: "#22c55e" },
    { label: "Pending", value: rsvpStats.pending, color: "#f59e0b" },
    { label: "Declined", value: rsvpStats.declined, color: "#ef4444" }
  ];
  
  // Conversion funnel data
  const conversionRate = preRsvpStats.total > 0 
    ? Math.round((preRsvpStats.converted / preRsvpStats.total) * 100) 
    : 0;
  
  // Colors for interests
  const INTEREST_COLORS = {
    "Fashion": "#ec4899",
    "Technology": "#3b82f6",
    "Art & Design": "#8b5cf6",
    "Business": "#10b981",
    "Music": "#f59e0b",
    "Investing": "#06b6d4",
    "Community": "#f43f5e",
    "Wellness": "#84cc16"
  };
  
  const DIETARY_COLORS = {
    "vegetarian": "#22c55e",
    "vegan": "#16a34a",
    "gluten_free": "#f59e0b",
    "dairy_free": "#3b82f6",
    "kosher": "#8b5cf6",
    "halal": "#ec4899",
    "pescatarian": "#06b6d4"
  };

  return (
    <div className="space-y-6">
      {/* View Toggle */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Event Insights</h2>
        <div className="flex gap-2">
          <Button 
            variant={activeView === "overview" ? "default" : "outline"} 
            size="sm"
            onClick={() => setActiveView("overview")}
            data-testid="insights-view-overview-btn"
          >
            <BarChart3 className="w-4 h-4 mr-1" />
            Overview
          </Button>
          <Button 
            variant={activeView === "audience" ? "default" : "outline"} 
            size="sm"
            onClick={() => setActiveView("audience")}
            data-testid="insights-view-audience-btn"
          >
            <Users className="w-4 h-4 mr-1" />
            Audience
          </Button>
          <Button
            variant={activeView === "preferences" ? "default" : "outline"}
            size="sm"
            onClick={() => setActiveView("preferences")}
            data-testid="insights-view-preferences-btn"
          >
            <Sparkles className="w-4 h-4 mr-1" />
            Preferences
          </Button>
        </div>
      </div>

      {activeView === "preferences" && (
        <PreferencesInsightsView
          data={prefInsights}
          loading={prefLoading}
          hideDietary={hideDietary}
        />
      )}
      
      {activeView === "overview" && (
        <>
          {/* Pre-RSVP Section */}
          {preRsvpStats.total > 0 && (
            <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <UserPlus className="w-4 h-4 text-purple-600" />
                      Pre-RSVP Signups
                    </CardTitle>
                    <CardDescription>Early interest before RSVPs opened</CardDescription>
                  </div>
                  <Badge className="bg-purple-100 text-purple-700">
                    {conversionRate}% converted
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center p-3 bg-white rounded-lg border">
                    <p className="text-2xl font-bold text-purple-600">{preRsvpStats.total}</p>
                    <p className="text-xs text-gray-500">Total Signups</p>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg border">
                    <p className="text-2xl font-bold text-green-600">{preRsvpStats.converted}</p>
                    <p className="text-xs text-gray-500">Converted</p>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg border">
                    <p className="text-2xl font-bold text-amber-600">{preRsvpStats.pending}</p>
                    <p className="text-xs text-gray-500">Pending</p>
                  </div>
                </div>
                
                {/* Interests from pre-invite */}
                {Object.keys(preRsvpStats.interests).length > 0 && (
                  <BreakdownBar 
                    label="Top Interests"
                    items={Object.entries(preRsvpStats.interests)
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 5)
                      .map(([label, count]) => ({
                        label,
                        count,
                        color: INTEREST_COLORS[label] || "#6b7280"
                      }))}
                  />
                )}
              </CardContent>
            </Card>
          )}
          
          {/* Main Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard 
              title="Total RSVPs"
              value={rsvpStats.total}
              subtitle={rsvpStats.totalAttending > rsvpStats.total ? `${rsvpStats.totalAttending} attending (incl. +1s)` : null}
              icon={Users}
              color="bg-blue-100 text-blue-600"
            />
            <StatCard 
              title="Confirmed"
              value={rsvpStats.confirmed}
              icon={CheckCircle2}
              color="bg-green-100 text-green-600"
            />
            {!hideDietary && (
            <StatCard 
              title="Dietary Needs"
              value={rsvpStats.withDietary}
              icon={Utensils}
              color="bg-orange-100 text-orange-600"
            />
            )}
            {!hideDietary && (
            <StatCard 
              title="Accessibility"
              value={rsvpStats.withAccessibility}
              icon={Accessibility}
              color="bg-purple-100 text-purple-600"
            />
            )}
          </div>
          
          {/* RSVP Status Chart */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">RSVP Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <DonutChart data={rsvpStatusData} />
                <div className="space-y-2 flex-1 ml-6">
                  {rsvpStatusData.filter(d => d.value > 0).map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                        <span className="text-sm">{item.label}</span>
                      </div>
                      <span className="font-medium">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Waitlist Section */}
          {waitlistStats.total > 0 && (
            <Card className="border-amber-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-600" />
                  Waitlist
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold">{waitlistStats.total}</p>
                    <p className="text-xs text-gray-500">On waitlist</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">{waitlistStats.converted}</p>
                    <p className="text-xs text-gray-500">Converted</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
      
      {activeView === "audience" && (
        <div className="space-y-6">
          {/* Full aggregate charts from RSVP data */}
          {(() => {
            const aggFields = [
              { key: "interests", label: "Interests", extract: r => r.preferences?.interests || r.interests || [] },
              { key: "audience_type", label: "Audience Type", extract: r => r.audience_type || [] },
              { key: "city", label: "Location", extract: r => r.city ? [r.city] : [] },
              { key: "company", label: "Company", extract: r => r.company ? [r.company] : [] },
              { key: "title", label: "Role / Title", extract: r => r.title ? [r.title] : [] },
              { key: "stage", label: "Stage", extract: r => r.stage ? [r.stage] : [] },
              { key: "learning_preferences", label: "Learning Preferences", extract: r => r.learning_preferences || [] },
              { key: "vibe", label: "Vibe", extract: r => Array.isArray(r.vibe) ? r.vibe : r.vibe ? [r.vibe] : [] },
              { key: "accessibility_needs", label: "Accessibility Needs", extract: r => r.accessibility_needs || [] },
            ];
            if (!hideDietary) {
              aggFields.push({ key: "dietary_restrictions", label: "Dietary Restrictions", extract: r => r.dietary_restrictions || [] });
            }
            
            const BAR_COLORS = [
              "#6366f1", "#8b5cf6", "#3b82f6", "#10b981", "#f59e0b",
              "#ef4444", "#06b6d4", "#ec4899", "#84cc16", "#f97316", "#14b8a6"
            ];
            
            const charts = aggFields.map(field => {
              const breakdown = {};
              rsvps.forEach(r => {
                field.extract(r).forEach(val => {
                  if (val && typeof val === 'string' && val.trim()) {
                    const clean = val.trim().replace(/_/g, ' ');
                    breakdown[clean] = (breakdown[clean] || 0) + 1;
                  }
                });
              });
              return { ...field, data: breakdown };
            }).filter(c => Object.keys(c.data).length > 0);
            
            if (charts.length === 0) {
              return (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No audience data yet</p>
                    <p className="text-sm text-gray-400">Insights will appear as RSVPs come in</p>
                  </CardContent>
                </Card>
              );
            }
            
            return charts.map((chart, chartIdx) => {
              const entries = Object.entries(chart.data).sort((a, b) => b[1] - a[1]);
              const maxVal = Math.max(...entries.map(([, v]) => v));
              return (
                <Card key={chart.key} data-testid={`host-chart-${chart.key}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">{chart.label}</CardTitle>
                      <span className="text-xs text-gray-400">{entries.length} categories</span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {entries.map(([label, count], i) => {
                        const pct = rsvpStats.total > 0 ? Math.round((count / rsvpStats.total) * 100) : 0;
                        const barPct = maxVal > 0 ? (count / maxVal) * 100 : 0;
                        const color = BAR_COLORS[(chartIdx + i) % BAR_COLORS.length];
                        return (
                          <div key={label}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm text-gray-700 truncate max-w-[60%]">{label}</span>
                              <span className="text-xs font-medium text-gray-500 tabular-nums">{count} ({pct}%)</span>
                            </div>
                            <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all duration-700"
                                style={{ width: `${barPct}%`, backgroundColor: color }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            });
          })()}
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Preference-focused insights view (admin-sync'd, no repeat-rate noise)
// Reads buckets from /api/host/insights/preferences/{event_id}:
// top_preferences, allergy_frequency, top_looks, top_linked_products,
// inspo_source_breakdown, service_mix, accessibility, dietary.
// ─────────────────────────────────────────────────────────────

function InsightBucket({ title, icon: Icon, items, emptyLabel, renderRow }) {
  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-gray-500" />}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {(!items || items.length === 0) ? (
          <p className="text-xs text-gray-400">{emptyLabel}</p>
        ) : (
          <ul className="space-y-1.5">
            {items.slice(0, 6).map((row, i) => (renderRow ? renderRow(row, i) : (
              <li key={i} className="flex items-center justify-between text-xs">
                <span className="text-gray-700 truncate pr-2">{row.value}</span>
                <span className="font-semibold text-gray-900">{row.count}</span>
              </li>
            )))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function PreferencesInsightsView({ data, loading, hideDietary }) {
  if (loading) {
    return (
      <div className="text-center py-12 text-sm text-gray-500" data-testid="preferences-insights-loading">
        Loading preference insights...
      </div>
    );
  }
  if (!data || ((data.total_cards || 0) + (data.total_rsvps || 0)) === 0) {
    return (
      <Card data-testid="preferences-insights-empty">
        <CardContent className="pt-6 pb-6 text-center text-sm text-gray-500">
          <Sparkles className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          <p>No preference data yet.</p>
          <p className="text-xs text-gray-400 mt-1">
            Once clients complete intake responses, you&apos;ll see aggregated patterns here.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="preferences-insights-view">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="p-3">
          <p className="text-xs text-gray-500">Clients</p>
          <p className="text-xl font-bold" data-testid="insights-total-clients">{data.total_clients || 0}</p>
        </Card>
        <Card className="p-3">
          <p className="text-xs text-gray-500">Cards</p>
          <p className="text-xl font-bold">{data.total_cards || 0}</p>
        </Card>
        <Card className="p-3">
          <p className="text-xs text-gray-500">RSVPs</p>
          <p className="text-xl font-bold">{data.total_rsvps || 0}</p>
        </Card>
        <Card className="p-3">
          <p className="text-xs text-gray-500">Category</p>
          <p className="text-sm font-semibold uppercase">{data.category}</p>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        <InsightBucket
          title="Top preference patterns"
          icon={Heart}
          items={data.top_preferences}
          emptyLabel="No preference answers yet."
        />
        <InsightBucket
          title="Allergy frequency"
          icon={Utensils}
          items={data.allergy_frequency}
          emptyLabel="No allergies recorded."
        />
        <InsightBucket
          title="Top requested looks"
          icon={Eye}
          items={data.top_looks}
          emptyLabel="No look tags yet."
        />
        <InsightBucket
          title="Top linked products"
          icon={Sparkles}
          items={data.top_linked_products}
          emptyLabel="No linked products."
          renderRow={(row, i) => (
            <li key={i} className="flex items-center justify-between text-xs">
              <span className="text-gray-700 truncate pr-2">
                {row.product?.name || row.value}
                {row.product?.brand && <span className="text-gray-400"> · {row.product.brand}</span>}
              </span>
              <span className="font-semibold text-gray-900">{row.count}</span>
            </li>
          )}
        />
        <InsightBucket
          title="Inspo source"
          icon={Share}
          items={data.inspo_source_breakdown}
          emptyLabel="No inspo uploads."
        />
        <InsightBucket
          title="Service mix"
          icon={BarChart3}
          items={data.service_mix}
          emptyLabel="No service bookings yet."
        />
        {data.accessibility?.collected && (
          <InsightBucket
            title="Accessibility needs"
            icon={Accessibility}
            items={data.accessibility?.tally}
            emptyLabel="No accessibility data."
          />
        )}
        {!hideDietary && data.dietary?.collected && (
          <InsightBucket
            title="Dietary needs"
            icon={Utensils}
            items={data.dietary?.tally}
            emptyLabel="No dietary data."
          />
        )}
      </div>
    </div>
  );
}
